import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { verteilRegelnQuery, verteilVorschauQuery, verteilZieleQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { AufgabenTitel } from './AufgabeKontext'
import { Button, Card, CardPad } from './ui/primitives'

/**
 * **Verteilung** ([Z-07.14](../../../../docs/phasen/zusatz-07-workspaces.md), 26.08.2026) –
 * *wohin geht, was hereinkommt.*
 *
 * Nils: *„ich will ungern so wie früher 10 sep. Mailadressen für jeden workspace hinterlegen."*
 * Also ein Postfach, und die Zuordnung passiert hier. Zwei Blöcke, in der Reihenfolge, in der man
 * sie einrichtet:
 *
 * 1. **Ziel je Quelle** – die Vorgabe. „Alles aus dem Postfach nach Verteiler."
 * 2. **Verteilregeln** – die benannten Ausnahmen davon. „Aber alles von `@agentur.de` nach
 *    Marketing."
 *
 * Gelesen wird beim Import in umgekehrter Reihenfolge: **die Regel zuerst**, weil sie das
 * Speziellere ist. Stünde die Route vorn, wäre jede Regel wirkungslos, sobald für ihre Quelle
 * überhaupt etwas eingestellt ist – und das ist der Normalfall.
 */
export function Verteilung() {
  return (
    <>
      <QuellenZiele />
      <Verteilregeln />
    </>
  )
}

/** Der Platzhalter für „alle Bereiche dieser Quelle" – muss zu `ALLE_BEREICHE` im Schema passen. */
const ALLE_BEREICHE = '*'

/**
 * **Wohin jede Quelle importiert.**
 *
 * Für Mail gibt es nur die eine Zeile: eine Mail hat keinen Bereich. Für GitLab und NetCon steht
 * darunter je Bereich eine Ausnahme – „GitLab allgemein nach Entwicklung, aber das Projekt
 * `marketing/website` nach Marketing".
 *
 * **Ändert nichts am Bestand.** Was schon importiert ist, bleibt liegen, wo es liegt; die
 * Einstellung gilt ab dem nächsten Eingang. Alles andere wäre eine Sammelverschiebung, die
 * niemand angefordert hat.
 */
function QuellenZiele() {
  const queryClient = useQueryClient()
  const { data: quellen = [], error } = useQuery(verteilZieleQuery)
  const { data: welten = [] } = useQuery({
    queryKey: ['workspaces', 'alle'],
    queryFn: () => trpc.workspaces.alle.query(),
    retry: false,
  })

  const setzen = useMutation({
    mutationFn: (v: { sourceId: string; scope: string; workspaceId: string | null }) =>
      trpc.verteilung.zielSetzen.mutate(v),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['verteilung'] }),
  })

  if (error) return null

  /**
   * **Nur geteilte Arbeitswelten stehen zur Wahl.** In einen persönlichen Workspace zu
   * importieren hieße, fremde Post in eine Welt zu legen, die niemand sonst sehen kann – auch
   * der Admin nicht, der es eingestellt hat. Der Server weist es zusätzlich ab; hier steht es
   * gar nicht erst in der Liste.
   */
  const ziele = welten.filter((w) => w.art === 'geteilt' && w.isActive)

  return (
    <Card className="mt-3">
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-[13.5px] font-bold">Ziel je Quelle</h3>
          <span className="text-[11.5px] text-muted">
            Wo landet, was hereinkommt – ohne Angabe im Auffangworkspace
          </span>
        </div>

        {ziele.length === 0 ? (
          <p className="mt-2 text-[12.5px] text-muted">
            Es gibt nur eine geteilte Arbeitswelt – dann gibt es auch nichts zu verteilen.
          </p>
        ) : (
          <div className="mt-3 grid gap-3">
            {quellen.map((q) => (
              <div key={q.id} className="rounded-lg border border-line p-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[12.5px] font-semibold">{q.label}</span>
                  <span className="font-mono text-[11px] text-muted">{q.id}</span>
                  {!q.isActive && <span className="text-[11px] text-muted">· inaktiv</span>}
                  <div className="ml-auto flex items-center gap-1.5">
                    <span className="text-[11.5px] text-muted">alles nach</span>
                    <ZielWahl
                      wert={q.alleBereiche}
                      ziele={ziele}
                      ariaLabel={`Ziel für ${q.label}`}
                      disabled={setzen.isPending}
                      setzen={(workspaceId) =>
                        setzen.mutate({ sourceId: q.id, scope: ALLE_BEREICHE, workspaceId })
                      }
                    />
                  </div>
                </div>

                {q.bereiche.length > 0 && (
                  <div className="mt-2 grid gap-1 border-t border-line pt-2">
                    {q.bereiche.map((b) => (
                      <div key={b.scope} className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-ink2">
                          {b.scope.replace('epcan-main/netcon/', '')}
                        </span>
                        <ZielWahl
                          wert={b.workspaceId}
                          ziele={ziele}
                          ariaLabel={`Ziel für ${b.scope}`}
                          disabled={setzen.isPending}
                          leer="wie die Quelle"
                          setzen={(workspaceId) =>
                            setzen.mutate({ sourceId: q.id, scope: b.scope, workspaceId })
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="mt-2 text-[11px] text-muted">
          Gilt ab dem nächsten Eingang. Was schon da ist, bleibt liegen, wo es liegt – umhängen geht
          im Aufgabendetail.
        </p>
      </CardPad>
    </Card>
  )
}

function ZielWahl({
  wert,
  ziele,
  ariaLabel,
  disabled,
  leer = 'Auffangworkspace',
  setzen,
}: {
  wert: string | null
  ziele: { id: string; label: string; icon: string }[]
  ariaLabel: string
  disabled?: boolean
  leer?: string
  setzen: (workspaceId: string | null) => void
}) {
  return (
    <select
      value={wert ?? ''}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => setzen(e.target.value || null)}
      className="cursor-pointer rounded-md border border-line px-1.5 py-0.5 text-[11.5px]"
    >
      <option value="">– {leer} –</option>
      {ziele.map((z) => (
        <option key={z.id} value={z.id}>
          {z.icon} {z.label}
        </option>
      ))}
    </select>
  )
}

const FELD_LABELS: Record<string, string> = {
  absender: 'Absender',
  absender_domain: 'Absender-Domain',
  empfaenger: 'Empfänger',
  titel: 'Betreff/Titel',
  text: 'Text',
  bereich: 'Bereich',
}

const OPERATOR_LABELS: Record<string, string> = {
  beginnt_mit: 'beginnt mit',
  enthaelt: 'enthält',
  ist_gleich: 'ist gleich',
}

/**
 * **Die Verteilregeln.**
 *
 * Dieselbe Bauform wie die Triage-Regeln – Rang, Bedingung, Trefferzähler, Pfeile zum Rücken.
 * Wer die eine Liste bedient hat, kann die andere. Zwei Unterschiede, beide mit Grund:
 *
 * | | Triage | Verteilung |
 * |---|---|---|
 * | **Stufen** | drei (vorschlagen → bestätigen → automatisch) | keine, nur an/aus |
 * | **Aktion** | Status, Pool, Person, Zeitfenster | genau eine: der Workspace |
 *
 * Die Stufen fehlen nicht aus Bequemlichkeit. Eine Triage-Regel stellt Aufgaben **zurück** –
 * greift sie zu breit, verschwindet echte Arbeit aus den Pools. Eine Verteilregel versteckt
 * nichts; sie entscheidet, wo etwas **entsteht**, sichtbar für die Mitglieder dieser Welt und mit
 * einem Klick umzuhängen. Und ein „Vorschlag" wäre hier ohnehin nur ein Wort: die Regel läuft im
 * Importeur um 03:00 Uhr, es ist niemand da, den man fragen könnte.
 *
 * Die Sicherung steht deshalb **vor** dem Speichern: die Vorschau zeigt am vorhandenen Bestand,
 * was die Regel getroffen hätte.
 */
function Verteilregeln() {
  const queryClient = useQueryClient()
  const { data: regeln = [], error } = useQuery(verteilRegelnQuery)
  const [anlegenOffen, setAnlegenOffen] = useState(false)

  const neuLaden = () => void queryClient.invalidateQueries({ queryKey: ['verteilung'] })

  const aktivSetzen = useMutation({
    mutationFn: (v: { id: string; aktiv: boolean }) => trpc.verteilung.aktivSetzen.mutate(v),
    onSuccess: neuLaden,
  })
  const verschieben = useMutation({
    mutationFn: (v: { id: string; richtung: 'hoch' | 'runter' }) =>
      trpc.verteilung.verschieben.mutate(v),
    onSuccess: neuLaden,
  })
  const loeschen = useMutation({
    mutationFn: (id: string) => trpc.verteilung.loeschen.mutate({ id }),
    onSuccess: neuLaden,
  })

  if (error) return null

  return (
    <Card className="mt-3">
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-[13.5px] font-bold">Verteilregeln</h3>
          <span className="text-[11.5px] text-muted">
            Die Ausnahmen von der Vorgabe – sie greifen vor der Triage
          </span>
        </div>

        {regeln.length === 0 ? (
          <p className="mt-2 text-[12.5px] text-muted">
            Keine Regeln hinterlegt – alles läuft nach dem Ziel der Quelle.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[620px] mt-3 w-full border-collapse text-[12px]">
              <thead>
                <tr>
                  {['Rang', 'Regel', 'prüft', 'Ziel', 'Treffer', ''].map((h) => (
                    <th
                      key={h}
                      className="border-b border-line px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-[0.4px] text-muted uppercase"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {regeln.map((r, i) => (
                  <tr key={r.id} className={r.aktiv ? '' : 'opacity-50'}>
                    {/**
                     * **Die Position, nicht die gespeicherte Zahl** – wie bei der Triage.
                     * `reihenfolge` ist eine Rechengröße mit Lücken; was zählt, ist *wer vor wem*.
                     */}
                    <td className="border-b border-line px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <span className="tabular w-4 text-muted">{i + 1}</span>
                        <button
                          type="button"
                          disabled={i === 0 || verschieben.isPending}
                          onClick={() => verschieben.mutate({ id: r.id, richtung: 'hoch' })}
                          title="früher prüfen"
                          aria-label={`${r.name} nach oben`}
                          className="cursor-pointer rounded px-1 leading-none text-ink2 hover:bg-[#f3f2ef] disabled:cursor-default disabled:opacity-25"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          disabled={i === regeln.length - 1 || verschieben.isPending}
                          onClick={() => verschieben.mutate({ id: r.id, richtung: 'runter' })}
                          title="später prüfen"
                          aria-label={`${r.name} nach unten`}
                          className="cursor-pointer rounded px-1 leading-none text-ink2 hover:bg-[#f3f2ef] disabled:cursor-default disabled:opacity-25"
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                    <td className="border-b border-line px-2 py-1.5">
                      <strong>{r.name}</strong>
                    </td>
                    <td className="border-b border-line px-2 py-1.5 font-mono text-[11px]">
                      {FELD_LABELS[r.feld] ?? r.feld} {OPERATOR_LABELS[r.operator] ?? r.operator} „
                      {r.wert}"{r.sourceId && <span className="text-muted"> · {r.sourceId}</span>}
                    </td>
                    <td className="border-b border-line px-2 py-1.5">
                      {r.zielIcon} {r.zielLabel}
                    </td>
                    <td className="tabular border-b border-line px-2 py-1.5">
                      {r.treffer === 0 ? (
                        <span className="text-muted" title="noch nie gegriffen">
                          0
                        </span>
                      ) : (
                        <span
                          title={
                            r.letzterTreffer
                              ? `zuletzt ${new Date(r.letzterTreffer).toLocaleString('de-DE')}`
                              : undefined
                          }
                        >
                          {r.treffer}
                        </span>
                      )}
                    </td>
                    <td className="border-b border-line px-2 py-1.5">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          onClick={() => aktivSetzen.mutate({ id: r.id, aktiv: !r.aktiv })}
                        >
                          {r.aktiv ? 'abschalten' : 'einschalten'}
                        </Button>
                        {/**
                         * **Löschen nur, solange sie nie gegriffen hat** – der Server weist es
                         * sonst ab. Der Knopf steht trotzdem da: eine Regel, die man nicht
                         * loswird, soll wenigstens sagen, warum.
                         */}
                        {r.treffer === 0 && (
                          <Button variant="ghost" onClick={() => loeschen.mutate(r.id)}>
                            löschen
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {loeschen.data?.grund && (
          <p className="mt-2 text-[11.5px] text-warn">{loeschen.data.grund}</p>
        )}

        <div className="mt-3">
          <Button variant="ghost" onClick={() => setAnlegenOffen((v) => !v)}>
            {anlegenOffen ? 'Anlegen abbrechen' : '+ Neue Verteilregel'}
          </Button>
        </div>
        {anlegenOffen && <VerteilregelAnlegen fertig={() => setAnlegenOffen(false)} />}

        <p className="mt-2 text-[11px] text-muted">
          Die erste greifende Regel gewinnt. Absender, Domain und Empfänger gibt es nur bei Mail –
          bei GitLab und NetCon greifen Regeln darauf nie.
        </p>
      </CardPad>
    </Card>
  )
}

/**
 * **Eine Verteilregel anlegen – mit Vorschau, bevor sie gilt.**
 *
 * Die Vorschau ist hier kein Komfort, sondern der einzige Schutz: Verteilregeln kennen keine
 * Stufe „vorschlagen". Sie läuft über den vorhandenen Bestand und zeigt die **Titel** – „14
 * Treffer" beantwortet die Frage nicht, die vor dem Speichern zählt: *ist das wirklich alles
 * Marketing?*
 */
function VerteilregelAnlegen({ fertig }: { fertig: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [feld, setFeld] = useState('absender_domain')
  const [operator, setOperator] = useState('ist_gleich')
  const [wert, setWert] = useState('')
  const [sourceId, setSourceId] = useState<string>('work_mail')
  const [ziel, setZiel] = useState('')

  const { data: quellen = [] } = useQuery(verteilZieleQuery)
  const { data: welten = [] } = useQuery({
    queryKey: ['workspaces', 'alle'],
    queryFn: () => trpc.workspaces.alle.query(),
    retry: false,
  })
  const ziele = welten.filter((w) => w.art === 'geteilt' && w.isActive)

  const entwurf = { feld, operator, wert: wert.trim(), sourceId: sourceId || null }
  const { data: vorschau } = useQuery({
    ...verteilVorschauQuery(entwurf),
    enabled: entwurf.wert.length >= 2,
  })

  const anlegen = useMutation({
    mutationFn: () =>
      trpc.verteilung.anlegen.mutate({
        name: name.trim(),
        feld: feld as 'absender',
        operator: operator as 'ist_gleich',
        wert: entwurf.wert,
        sourceId: entwurf.sourceId,
        zielWorkspaceId: ziel,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['verteilung'] })
      fertig()
    },
  })

  const vollstaendig = name.trim().length >= 3 && entwurf.wert.length >= 2 && ziel !== ''

  return (
    <div className="mt-3 rounded-lg border border-line bg-[#fafaf8] p-3">
      <div className="grid gap-2 text-[12px] sm:grid-cols-2">
        <label className="grid gap-0.5">
          <span className="text-[11px] text-muted">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Agentur-Mails nach Marketing"
            className="rounded-md border border-line px-1.5 py-1"
          />
        </label>
        <label className="grid gap-0.5">
          <span className="text-[11px] text-muted">Ziel-Arbeitswelt</span>
          <select
            value={ziel}
            onChange={(e) => setZiel(e.target.value)}
            className="cursor-pointer rounded-md border border-line px-1.5 py-1"
          >
            <option value="">– wohin? –</option>
            {ziele.map((z) => (
              <option key={z.id} value={z.id}>
                {z.icon} {z.label}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-0.5">
          <span className="text-[11px] text-muted">prüft</span>
          <select
            value={feld}
            onChange={(e) => setFeld(e.target.value)}
            className="cursor-pointer rounded-md border border-line px-1.5 py-1"
          >
            {Object.entries(FELD_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-0.5">
          <span className="text-[11px] text-muted">Vergleich</span>
          <select
            value={operator}
            onChange={(e) => setOperator(e.target.value)}
            className="cursor-pointer rounded-md border border-line px-1.5 py-1"
          >
            {Object.entries(OPERATOR_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-0.5">
          <span className="text-[11px] text-muted">Wert</span>
          <input
            value={wert}
            onChange={(e) => setWert(e.target.value)}
            placeholder="agentur.de"
            className="rounded-md border border-line px-1.5 py-1"
          />
        </label>
        <label className="grid gap-0.5">
          <span className="text-[11px] text-muted">nur für Quelle</span>
          <select
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            className="cursor-pointer rounded-md border border-line px-1.5 py-1"
          >
            <option value="">– alle Quellen –</option>
            {quellen.map((q) => (
              <option key={q.id} value={q.id}>
                {q.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/**
       * **Was die Regel getroffen hätte** – am vorhandenen Bestand, bevor sie gespeichert wird.
       * Die Titel sind der Punkt: eine Zahl sagt nichts darüber, ob echte Fremdarbeit darunter
       * ist.
       */}
      {entwurf.wert.length >= 2 && (
        <div className="mt-3 border-t border-line pt-2 text-[12px]">
          {!vorschau ? (
            <p className="text-muted">wird geprüft …</p>
          ) : vorschau.anzahl === 0 ? (
            <p className="text-muted">
              Keine der {vorschau.geprueft} geprüften Aufgaben passt. Das kann richtig sein – oder
              die Regel greift nie.
            </p>
          ) : (
            <>
              <p className="mb-1.5">
                <strong>{vorschau.anzahl}</strong> von {vorschau.geprueft} Aufgaben hätte diese
                Regel erwischt – <em>nichts davon wird verschoben.</em>
              </p>
              <ul className="grid gap-1">
                {vorschau.treffer.map((t) => (
                  <li key={t.taskId} className="flex gap-2">
                    <span className="shrink-0 text-muted">{t.workspaceId}</span>
                    <AufgabenTitel aufgabeId={t.taskId} titel={t.titel} className="text-ink2" />
                  </li>
                ))}
              </ul>
              {vorschau.anzahl > vorschau.treffer.length && (
                <p className="mt-1 text-[11px] text-muted">
                  … und {vorschau.anzahl - vorschau.treffer.length} weitere
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button disabled={!vollstaendig || anlegen.isPending} onClick={() => anlegen.mutate()}>
          {anlegen.isPending ? 'wird angelegt …' : 'Regel anlegen'}
        </Button>
        <Button variant="ghost" onClick={fertig}>
          Abbrechen
        </Button>
        {anlegen.error && <span className="text-[11.5px] text-warn">{anlegen.error.message}</span>}
      </div>
      <p className="mt-2 text-[11px] text-muted">
        Die Regel gilt ab dem nächsten Eingang. Vorhandene Aufgaben bleiben, wo sie sind.
      </p>
    </div>
  )
}
